// Copyright (c) Docugami, Inc. All rights reserved.

import { IGroup, Shimmer, ShimmerElementType } from '@fluentui/react';
import { LargeUIBold, tagFont } from 'components/common/Fonts';
import { IReportRow } from './AgGridReportTable';
import { appContentColor, emptyGray, greyText, primaryTheme } from 'components/shell/presentation/AppStyles';
import styled, { css } from 'styled-components';

const CellDisabledCss = css`
    * {
        cursor: default;
        pointer-events: none;
    }
`;

export type CellStyles = {
    root?: string[];
}

export const firstOfGroupClassName = "first-of-group";
export const lastOfGroupClassName = "last-of-group";
export const groupHeaderClassName = "group-header";
export const groupFooterClassName = "group-footer";

export const CellContentContainer = styled.div.attrs(props => ({
    'data-componentname': 'CellContentContainer',
    className: props.className,
})) <{
    disabled?: boolean;
    showBottomBorder?: boolean;
}>`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 5px;
    box-sizing: border-box;
    position: relative;

    ${props => !!props.disabled && CellDisabledCss}

    ${props => props.showBottomBorder && `
        border-bottom: 1px solid;
    `}

    &.${groupHeaderClassName} {
        border-top: 3px solid ${greyText};
    }
`

export const SpinnerContainer = styled.div.attrs({
    'data-componentname': 'SpinnerContainer'
})`
    display: flex;
    width: fit-content;
    padding: 5px;
    box-sizing: border-box;
`

export const HoverOverflowCss = css`
    .ms-OverflowSet-overflowButton {
        visibility: hidden
    }

    :hover {
        .ms-OverflowSet-overflowButton {
            visibility: visible;
        }
    }
`;

export const LoadingCellContent = (): JSX.Element => {
    return (
        <>
            <Shimmer isDataLoaded={false} width="85%" shimmerElements={[{ type: ShimmerElementType.line, height: 16 }]} style={{ margin: "10px 10px 3px" }} />
            <Shimmer isDataLoaded={false} width="90%" shimmerElements={[{ type: ShimmerElementType.line, height: 16 }]} style={{ marginLeft: "10px" }} />
        </>
    )
}

export const ReportHeaderText = styled(LargeUIBold).attrs({
    'data-componentname': 'ReportHeaderText',
})`
    font-size: 18px;
    line-height: 20px;
    width: 80%;
    text-align: center;
    padding-left: 10%;
`;

const DisabledPointerCss = css`
    pointer-events: none;
`

export const GroupTitleButton = styled.button.attrs({
    'data-componentname': 'GroupTitleButton',
})`
    position: absolute;
    top: 0;
    left: 0;

    font-family: ${tagFont};
    font-size: 13px;
    padding: 3px 5px;
    box-sizing: border-box;
    background-color: ${greyText};
    border-bottom-right-radius: 4px;
    color: ${primaryTheme.palette.white};
    border-width: 0;

    :hover {
        background-color: ${emptyGray};
        cursor: pointer;
        :active {
            background-color: ${greyText};
        }
    }

    max-width: 85%;
    text-overflow: ellipsis;
    overflow: hidden;
    ${props => !!props.disabled && DisabledPointerCss};
`

export const TitleCellContainer = styled.div.attrs({
    'data-componentname': 'TitleCellContainer',
})`
    display: flex;
    flex-direction: row;
    align-items: stretch;
    width: 100%;
    height: 100%;
    background-color: white;
    border-radius: 5px;
    ${HoverOverflowCss}
`;

export const ColumnContainer = styled.div.attrs({
    'data-componentname': 'ColumnContainer',
})`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
`;

export const CenterColumnContainer = styled.div.attrs({
    'data-componentname': 'CenterColumnContainer',
})`
    display: flex;
    flex-direction: column;
    justify-content: center;

    flex-grow: 1;
    max-width: 90%;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`;

export const ControlsColumnContainer = styled.div.attrs({
    'data-componentname': 'ControlsColumnContainer',
})`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-end;
    padding: 4px;
`;

export const OverflowColumnContainer = styled(ColumnContainer).attrs({
    'data-componentname': 'OverflowColumnContainer',
})`
    width: 24px;
    justify-content: flex-start;
`;

export const EntryHeadingText = styled.div.attrs({
    'data-componentname': 'EntryHeadingText',
})`
    color: ${appContentColor};
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
`;

export function getCellStylesForGroupedCell(group: IGroup, gridRows: IReportRow[], columnIndex: number, rowIndex?: number): string[] {
    const groupStartIndex = group.startIndex;
    const groupEndIndex = groupStartIndex + group.count - 1;

    const cellStyles: string[] = [];
    if (groupStartIndex === columnIndex) {
        cellStyles.push(firstOfGroupClassName)
    }
    if (groupEndIndex === columnIndex) {
        cellStyles.push(lastOfGroupClassName)
    }
    if (rowIndex === undefined) {
        cellStyles.push(groupHeaderClassName)
    }
    if (rowIndex === gridRows.length - 1) {
        cellStyles.push(groupFooterClassName)
    }
    return cellStyles;
}
